import { useId } from "react"
import type { IntentConfidence } from "../../types"

type ConfidenceMeterProps = {
  confidence: IntentConfidence
}

export function ConfidenceMeter({ confidence }: ConfidenceMeterProps) {
  const headingId = useId()
  const score = typeof confidence.score === "number" ? confidence.score : 0
  const percent = Math.round(Math.max(0, Math.min(1, score)) * 100)
  const label = confidence.label?.toUpperCase?.() ?? "UNKNOWN"
  const color = pickConfidenceColor(confidence.label)
  const reasons = confidence.reasons ?? []
  const degraded = Boolean(confidence.degraded || confidence.label === "low")

  return (
    <section
      role="group"
      aria-labelledby={headingId}
      aria-live="polite"
      style={{
        border: "1px solid color-mix(in srgb, var(--text) 16%, transparent)",
        background: "color-mix(in srgb, var(--panel) 72%, transparent)",
        padding: "14px 16px",
        display: "grid",
        gap: "10px",
      }}
    >
      <header id={headingId} className="mono" style={{ letterSpacing: "0.12em", textTransform: "uppercase" }}>
        Intent confidence
      </header>
      <div style={{ display: "grid", gap: "6px" }}>
        <div
          className="mono"
          role="status"
          aria-live="polite"
          aria-label={`Intent confidence ${label} at ${percent} percent`}
          style={{ display: "flex", gap: "8px", alignItems: "center" }}
        >
          <span style={{ letterSpacing: "0.1em", color }}>{label}</span>
          <span className="tiny mono" style={{ color: "var(--muted)" }}>{percent}%</span>
        </div>
        <div
          aria-hidden
          style={{
            width: "100%",
            height: "8px",
            borderRadius: "4px",
            background: "color-mix(in srgb, var(--text) 8%, transparent)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${percent}%`,
              height: "100%",
              background: color,
              transition: "width 200ms ease",
            }}
          />
        </div>
      </div>
      {reasons.length ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {reasons.map((reason) => (
            <span
              key={reason}
              className="tiny mono"
              style={{
                border: "1px solid color-mix(in srgb, var(--text) 18%, transparent)",
                padding: "2px 6px",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              {reason.replaceAll("_", " ")}
            </span>
          ))}
        </div>
      ) : null}
      {degraded ? (
        <div className="small" style={{ color: "var(--error-text)", textTransform: "none" }}>
          Low confidence — double-check critical fields before sending.
        </div>
      ) : null}
      {confidence.evaluated_at ? (
        <div className="tiny mono" style={{ color: "var(--muted)" }}>
          Evaluated {new Date(confidence.evaluated_at).toLocaleString()}
        </div>
      ) : null}
    </section>
  )
}

function pickConfidenceColor(label: string | undefined) {
  switch ((label ?? "").toLowerCase()) {
    case "high":
      return "var(--success-text)"
    case "medium":
      return "var(--warning-text)"
    case "low":
      return "var(--error-text)"
    default:
      return "var(--text-faint)"
  }
}
